import React from 'react';
import { Box, styled } from '@mui/material';

const SWIPE_THRESHOLD = 180;
const SCROLL_THRESHOLD = 15;
const SWIPE_ICON_THRESHOLD = 45;

const Wrapper = styled(Box)({
    position: 'relative',
    width: '100%'
});

const BaseSwipeable = styled(Box)({
    position: 'absolute',
    top: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    height: '100%',
    zIndex: 0
});

const LeftContent = styled(BaseSwipeable)({
    left: 0
});

const RightContent = styled(BaseSwipeable)({
    right: 0
});

type TouchStart = {
    x: number;
    y: number;
};

type IProps = {
    enableRightToLeft?: boolean;
    leftToRightIcon?: React.ReactNode;
    leftToRightBg?: string;
    rightToLeftIcon?: React.ReactNode;
    rightToLeftBg?: string;
    onSwipeLeft?: () => void;
    onSwipeRight?: () => void;
    enableLeftToRight?: boolean;
    children: React.ReactNode;
};

export const SwipeableListItem = ({
    enableRightToLeft,
    leftToRightIcon,
    rightToLeftIcon,
    enableLeftToRight,
    leftToRightBg,
    rightToLeftBg,
    onSwipeLeft,
    onSwipeRight,
    children
}: IProps) => {
    const [touchStart, setTouchStart] = React.useState<TouchStart | null>(null);
    const [isScrolling, setIsScrolling] = React.useState(false);

    const [dragX, setDragX] = React.useState<number | null>(null);
    const [positionX, setPositionX] = React.useState<number>(0);

    const rightWidth = React.useMemo(() => (positionX < 0 ? Math.abs(positionX) : 0), [positionX]);

    const opacity = React.useMemo(() => (Math.abs(positionX) / 100).toFixed(2), [positionX]);

    const isSwipingRight = React.useMemo(() => positionX > 0, [positionX]);

    const isSwipingLeft = React.useMemo(() => positionX < 0, [positionX]);

    const handleTouchStart = React.useCallback((event: React.TouchEvent<HTMLDivElement>) => {
        const startDragX = event.changedTouches[0].clientX;
        setDragX(startDragX);
        setTouchStart({
            x: event.touches[0].clientX,
            y: event.touches[0].clientY
        });
        setIsScrolling(false);
    }, []);

    const handleTouchMove = React.useCallback(
        (event: React.TouchEvent<HTMLDivElement>) => {
            if (!touchStart) {
                return;
            }

            const currentTouch = {
                x: event.touches[0].clientX,
                y: event.touches[0].clientY
            };

            const deltaX = currentTouch.x - touchStart.x;
            const deltaY = currentTouch.y - touchStart.y;

            // Determine if this is vertical scrolling
            if (Math.abs(deltaY) > SCROLL_THRESHOLD && Math.abs(deltaY) > Math.abs(deltaX)) {
                setIsScrolling(true);
                return;
            }

            const positionX = event.changedTouches[0].clientX;
            if (dragX) {
                const newPositionX = positionX - dragX;
                // Only allow swiping if the direction is allowed
                // Right to left negative, left to right positive
                if (newPositionX < 0 && !enableRightToLeft) {
                    setPositionX(0);
                } else if (newPositionX > 0 && !enableLeftToRight) {
                    setPositionX(0);
                } else {
                    setPositionX(newPositionX);
                }
            }
        },
        [dragX, touchStart, enableRightToLeft, enableLeftToRight]
    );

    const handleTouchEnd = React.useCallback(
        (event: React.TouchEvent<HTMLDivElement>) => {
            if (isScrolling) {
                resetTouch();
                return;
            }

            const endDragX = event.changedTouches[0].clientX;
            if (dragX && Math.abs(dragX - endDragX) > SWIPE_THRESHOLD) {
                if (endDragX > dragX) {
                    onSwipeRight?.();
                } else {
                    onSwipeLeft?.();
                }
                resetTouch();
            }
            setTimeout(() => {
                resetTouch();
            }, 100);
        },
        [dragX, onSwipeRight, onSwipeLeft, isScrolling]
    );

    const resetTouch = () => {
        setPositionX(0);
        setTouchStart(null);
        setIsScrolling(false);
    };

    return (
        <Wrapper>
            {enableLeftToRight && isSwipingRight && (
                <LeftContent
                    style={{
                        width: `${positionX}px`,
                        opacity,
                        background: leftToRightBg
                    }}
                >
                    {positionX > SWIPE_ICON_THRESHOLD && leftToRightIcon}
                </LeftContent>
            )}
            <div
                style={{
                    transform: enableRightToLeft || enableLeftToRight ? `translateX(${positionX}px)` : 'none',
                    boxSizing: 'border-box',
                    touchAction: isSwipingLeft || isSwipingRight ? 'none' : 'pan-y'
                }}
                onTouchMove={handleTouchMove}
                onTouchStart={handleTouchStart}
                onTouchEnd={handleTouchEnd}
            >
                {children}
            </div>
            {enableRightToLeft && isSwipingLeft && (
                <RightContent
                    style={{
                        width: `${rightWidth}px`,
                        opacity,
                        background: rightToLeftBg
                    }}
                >
                    {positionX < -SWIPE_ICON_THRESHOLD && rightToLeftIcon}
                </RightContent>
            )}
        </Wrapper>
    );
};
